import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { formatInTimeZone } from "date-fns-tz";
import generateExcel from "zipcelx";
import { getEventRsvpSettings } from "../api/rsvp.js";
import useAuth from "../hooks/useAuth.js";
import { canAccessEventRsvps } from "../utils/eventPermissions.js";
import { getRsvpAnswers } from "../utils/rsvpAnswers.js";
import { getExcelCell } from "./Table.js";
import EventRsvpSettingsModal from "./EventRsvpSettingsModal.js";

const deadlineDate = (value) => {
  if (!value) return null;
  const normalized = typeof value === "string" && /^\d{4}-\d{2}-\d{2} /.test(value)
    ? `${value.replace(" ", "T")}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

export default function EventRsvpManagerModal({ show, onHide, eventId, eventTimezone = "America/Sao_Paulo", registrationEndDate, refreshKey, onRegistryChange, onSaved }) {
  const userState = useAuth();
  const canManage = canAccessEventRsvps(userState, 2);
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState("");
  const [editor, setEditor] = useState({ show: false, definitionId: undefined, sequence: 0 });
  const [modelsDefinition, setModelsDefinition] = useState(null);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [modelsReload, setModelsReload] = useState(0);
  const onRegistryChangeRef = useRef(onRegistryChange);
  onRegistryChangeRef.current = onRegistryChange;

  useEffect(() => {
    if (!show) {
      setEditor((current) => ({ ...current, show: false }));
      setModelsDefinition(null);
      setSearch("");
    }
  }, [show]);

  useEffect(() => {
    setRegistry(null);
    setSearch("");
    setEditor({ show: false, definitionId: undefined, sequence: 0 });
    setModelsDefinition(null);
  }, [eventId]);

  useEffect(() => {
    if (!show || !canManage || !eventId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getEventRsvpSettings({ eventId, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        const loaded = response.data?.registry;
        if (!Array.isArray(loaded?.definitions)) throw new Error("RSVP registry unavailable");
        setRegistry(loaded);
        onRegistryChangeRef.current?.(loaded);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure.response?.data?.message || "Não foi possível carregar os RSVPs do evento.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [canManage, eventId, refreshKey, reload, show]);

  useEffect(() => {
    if (!show || !canManage || !modelsDefinition) return undefined;
    const controller = new AbortController();
    setModels([]);
    setModelsError("");
    setModelsLoading(true);
    axios.get(`/api/event/email-templates/${Number(eventId)}`, { params: { rsvp_definition_id: modelsDefinition.definition_id }, signal: controller.signal })
      .then((response) => { if (!controller.signal.aborted) setModels(response.data?.message || []); })
      .catch((failure) => { if (!controller.signal.aborted) setModelsError(failure.response?.data?.message || "Não foi possível carregar os modelos de e-mail associados."); })
      .finally(() => { if (!controller.signal.aborted) setModelsLoading(false); });
    return () => controller.abort();
  }, [canManage, eventId, modelsDefinition, modelsReload, show]);

  const rows = useMemo(() => (registry?.definitions || []).map((definition) => {
    const deadline = deadlineDate(definition.deadline_at);
    return {
      ...definition,
      question: definition.presentation?.prompt?.desc || definition.presentation?.prompt?.title || "",
      answers: getRsvpAnswers(definition).map((answer) => answer.label).join(" / "),
      deadline: deadline ? formatInTimeZone(deadline, eventTimezone, "dd/MM/yyyy HH:mm") : "Sem prazo",
      availability: !deadline ? "Sem prazo" : deadline.getTime() <= Date.now() ? "Prazo encerrado" : "No prazo",
    };
  }), [eventTimezone, registry]);
  const visibleRows = useMemo(() => {
    const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const query = normalize(search.trim());
    return rows.filter((row) => normalize(`${row.name} ${row.question} ${row.answers}`).includes(query));
  }, [rows, search]);

  const openEditor = (definitionId) => setEditor((current) => ({ show: true, definitionId, sequence: current.sequence + 1 }));
  const exportDefinitions = () => generateExcel({
    filename: `RSVPs - ${eventId}`,
    sheet: {
      data: [
        ["RSVP", "Confirmação", "Respostas", "Prazo", "Situação", "Confirmação principal de participação"].map(getExcelCell),
        ...visibleRows.map((row) => [row.name, row.question, row.answers, row.deadline, row.availability, row.definition_id === registry.attendance_definition_id ? "Sim" : "Não"].map(getExcelCell)),
      ],
    },
  });

  if (!canManage) return null;

  return <>
    <Modal show={show && !editor.show} onHide={onHide} size="lg" centered scrollable fullscreen="sm-down" aria-labelledby="rsvp-manager-title">
      <Modal.Header closeButton><Modal.Title id="rsvp-manager-title">Gerenciar RSVPs</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <Form.Control type="search" className="flex-grow-1 w-auto" placeholder="Pesquisar RSVPs…" aria-label="Pesquisar RSVPs" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Button variant="outline-primary" disabled={loading || Boolean(error) || !visibleRows.length} onClick={exportDefinitions}>Exportar</Button>
          <Button variant="primary" disabled={loading || Boolean(error)} onClick={() => openEditor(undefined)}><FontAwesomeIcon icon={faPlus} className="me-2" />Criar RSVP</Button>
        </div>
        {error && <Alert variant="danger"><div>{error}</div><Button size="sm" variant="outline-danger" className="mt-2" onClick={() => setReload((value) => value + 1)}>Tentar novamente</Button></Alert>}
        {registry?.definitions?.length > 1 && !registry.attendance_definition_id && <Alert variant="warning" className="p-2">Defina, na edição de um RSVP, qual será a confirmação principal de participação.</Alert>}
        {loading ? <div role="status" className="text-muted py-3"><Spinner animation="border" size="sm" className="me-2" />Carregando RSVPs…</div> : !error && <>
          <div className="small text-muted text-end mb-2">Mostrando {visibleRows.length} de {rows.length} RSVPs</div>
          {visibleRows.length ? <ListGroup as="ul" aria-label="RSVPs do evento">
            {visibleRows.map((row) => <ListGroup.Item as="li" key={row.definition_id} className="mb-2">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div className="flex-grow-1 text-break">
                  <h6 className="mb-1">{row.name || "RSVP sem nome"}</h6>
                  {row.question && <p className="small text-muted mb-1">{row.question}</p>}
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {row.definition_id === registry.attendance_definition_id && <Badge bg="primary" text="dark">Confirmação de participação</Badge>}
                    <Badge bg={row.availability === "No prazo" ? "success" : "secondary"}>{row.availability}</Badge>
                  </div>
                </div>
                <div className="text-sm-end">
                  <div className="small text-muted mb-2">Prazo: {row.deadline}</div>
                  <div className="d-flex gap-2 flex-wrap justify-content-sm-end">
                    <Button size="sm" variant="primary" title="Modelos de e-mail associados" aria-label={`Ver modelos de e-mail associados ao RSVP ${row.name}`} onClick={() => setModelsDefinition(row)}><FontAwesomeIcon icon={faEnvelope} /></Button>
                    <Button size="sm" variant="primary" title="Editar RSVP" aria-label={`Editar RSVP ${row.name}`} onClick={() => openEditor(row.definition_id)}><FontAwesomeIcon icon={faPencil} /></Button>
                  </div>
                </div>
              </div>
            </ListGroup.Item>)}
          </ListGroup> : <div className="border rounded p-4 text-center text-muted">{rows.length ? "Nenhum RSVP encontrado com esta busca." : "Nenhum RSVP cadastrado. Use Criar RSVP para começar."}</div>}
        </>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fechar</Button></Modal.Footer>
    </Modal>
    <Modal show={show && Boolean(modelsDefinition)} onHide={() => setModelsDefinition(null)} size="sm" centered scrollable aria-labelledby="rsvp-email-models-title">
      <Modal.Header closeButton><Modal.Title id="rsvp-email-models-title">Modelos de e-mail</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="small text-muted">RSVP: <span className="fw-semibold text-break">{modelsDefinition?.name}</span></p>
        <p className="small text-muted">Modelos que utilizam este RSVP no conteúdo atual ou em uma versão salva.</p>
        {modelsLoading ? <div role="status" className="text-muted py-3"><Spinner animation="border" size="sm" className="me-2" />Carregando modelos…</div> : modelsError ? <Alert variant="danger"><div>{modelsError}</div><Button size="sm" variant="outline-danger" className="mt-2" onClick={() => setModelsReload((value) => value + 1)}>Tentar novamente</Button></Alert> : models.length ? <ListGroup as="ul">
          {models.map((template) => <ListGroup.Item as="li" key={template.template_id} className="mb-2">
            <div className="fw-bold text-break">{template.name}</div>
            <div className="small text-muted text-break">{template.subject || "Sem assunto"}</div>
            <div className="small mt-1">Versões com este RSVP: {(template.rsvp_versions || []).map((version) => `v${Number(version).toFixed(1)}`).join(", ") || "—"}</div>
          </ListGroup.Item>)}
        </ListGroup> : <p className="text-muted mb-0">Nenhum modelo de e-mail utiliza este RSVP.</p>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={() => setModelsDefinition(null)}>Fechar</Button></Modal.Footer>
    </Modal>
    <EventRsvpSettingsModal
      key={`${eventId}:${editor.definitionId || "new"}:${editor.sequence}`}
      show={show && editor.show}
      onHide={() => setEditor((current) => ({ ...current, show: false }))}
      eventId={eventId}
      eventTimezone={eventTimezone}
      registrationEndDate={registrationEndDate}
      initialDefinitionId={editor.definitionId}
      onSaved={(saved, savedRegistry) => {
        if (savedRegistry) { setRegistry(savedRegistry); onRegistryChangeRef.current?.(savedRegistry); }
        setReload((value) => value + 1);
        onSaved?.(saved, savedRegistry);
      }}
    />
  </>;
}
